import clone from 'clone'
import update from 'immutability-helper'
import isEqual from 'lodash.isequal'

import {matchLatLon} from '../@opentripplanner/core-utils/src/map'
import {
    getDefaultQuery,
    getTripOptionsFromQuery
} from '../@opentripplanner/core-utils/src/query'
import {filterProfileOptions} from '../@opentripplanner/core-utils/src/profile'
import {getItem, removeItem, storeItem} from '../@opentripplanner/core-utils/src/storage'
import {getUserTimezone} from '../@opentripplanner/core-utils/src/time'
import {MainPanelContent, MobileScreens} from '../actions/ui'
import i18n from "../../i18nSetup";
import {configuredDirectionByLanguage} from '../language/direction'
//import { setQueryParam } from '../actions/form'

const MAX_RECENT_STORAGE = 5

// TODO: fire planTrip action if default query is complete/error-free

/**
 * Validates the initial state of the store. This is intended to mainly catch
 * configuration issues since a manually edited config file is loaded into the
 * initial state.
 * TODO: mabye it's a better idea to move all of this to a script that can do
 *  JSON Schema validation and other stuff.
 */
function validateInitalState(initialState) {
  //  const {config} = initialState

    const errors = []



    if (errors.length > 0) {
        throw new Error(
            errors.reduce(
                (message, error) => {
                    return `${message}\n- ${error.message}`
                },
                'Encountered the following configuration errors:'
            )
        )
    }
}



/**
 * Create the initial state of otp-react-redux using user-provided config, any
 * items in localStorage and a few defaults.
 */
export function getInitialState(userDefinedConfig, initialQuery) {
    const lang = getItem('userLanguage') || userDefinedConfig['language'] || 'he';
    storeItem('userLanguage', lang);
    const defaultConfig = {
        needStopInformation:true,
        autoPlan: false,
        debouncePlanTimeMs: 0,
        language: {},
        userLanguage: lang,
        dir: lang === "he" || lang === "ar" ? 'rtl' : 'ltr',
        isShowMarkers: 0,
        operators: [],
        realtimeEffectsDisplayThreshold: 120,
        routingTypes: [],
        stopViewer: {
            numberOfDepartures: 3, // per pattern
            // This is set to 345600 (four days) so that, for example, if it is Friday and
            // a route does not begin service again until Monday, we are showing its next
            // departure and it is not entirely excluded from display.
            timeRange: 46400 // nadav: 12h preview
        }
    }

    const config = Object.assign(defaultConfig, userDefinedConfig)
    updateTilesURLConfigforLanguage(config,lang);
    configuredDirectionByLanguage(lang)

    if (!config.homeTimezone) {
        config.homeTimezone = getUserTimezone()
        console.warn(
            `Config value 'homeTimezone' not configured for this webapp!\n
      This value is recommended in order to properly display stop times for
      users that are not in the timezone that the transit system is in. The
      detected user timezone of '${config.homeTimezone}' will be used. Hopefully
      that is the right one...`
        )
    }

    // Load user settings from local storage.
    // TODO: Make this work with settings fetched from alternative storage system
    //  (e.g., OTP backend middleware containing user profile system).
    // User overrides determine user's default mode/query parameters.
    const userOverrides = getItem('defaultQuery', {})
    // Combine user overrides with default query to get default search settings.
    const defaults = Object.assign(getDefaultQuery(config), userOverrides)
    // Whether to auto-refresh stop arrival times in the Stop Viewer.
    const autoRefreshStopTimes = getItem('autoRefreshStopTimes', true)
    // User's home and work locations
    const home = getItem('home')
    const work = getItem('work')
    // Whether recent searches and places should be tracked in local storage.
    const trackRecent = getItem('trackRecent', true)
    // Recent places used in trip plan searches.
    const recentPlaces = getItem('recent', [])
    // List of user's favorite stops.
    const favoriteStops = getItem('favoriteStops', [])
    // Recent trip plan searches (excluding time/date parameters to avoid complexity).
    const recentSearches = getItem('recentSearches', [])
    // Filter valid locations found into locations list.
    const locations = [home, work].filter(p => p)
    // TODO: parse and merge URL query params w/ default query

    // populate query by merging any provided query params w/ the default params
    const currentQuery = Object.assign(defaults)
    // Add configurable locations to home and work locations
    if (config.locations) {
        locations.push(...config.locations.map(l => ({...l, type: 'suggested'})))
    }
  //  let queryModes = currentQuery.mode.split(',')

    // If we are in 'ITINERARY' mode, ensure that one and only one access mode is selected
   // if (currentQuery.routingType === 'ITINERARY') {
 //       queryModes = ensureSingleAccessMode(queryModes)
 //   }
    let modes = {
        modes: {
            transitModes: [
                {
                    "mode": "BUS",
                    "label": i18n.t('bus')
                },
                {
                    "mode": "TRAM",
                    "label": i18n.t('maxStreetcar')
                },
                {
                    "mode": "RAIL",
                    "label": i18n.t('WES')
                },
                {
                    "mode": "GONDOLA",
                    "label": i18n.t('aerialTram')
                }
            ],
            accessModes: [
                {
                    "mode": "BICYCLE",
                    "label": i18n.t('transitAndBike')
                }
            ],
            bicycleModes: [
                {
                    "mode": "BICYCLE",
                    "label": "Own Bike",
                    "iconWidth": 18
                }
            ]
        }
    };
    Object.assign(config, modes)

    return {
        config,
        currentQuery,
        location: {
            currentPosition: {
                error: null,
                coords: null,
                fetching: false
            },
            sessionSearches: [],
            nearbyStops: []
        },
        user: {
            autoRefreshStopTimes,
            // Do not store from/to or date/time in defaults
            defaults: getTripOptionsFromQuery(defaults),
            favoriteStops,
            trackRecent,
            locations,
            recentPlaces,
            recentSearches
        },
        searches: {},
        transitIndex: {
            stops: {},
            trips: {},
            line: null,
            schedules :[],
            routeAlerts:{},
            stopAlerts:{},
            routeDirections: []
        },
        useRealtime: true,
        activeSearchId: 0,
        overlay: {
            bikeRental: {
                stations: []
            },
            carRental: {
                stations: []
            },
            parkAndRide: {
                // null default value indicates no request for P&R list has been made
                locations: null
            },
            transit: {
                stops: []
            },
            transitive: null,
            vehicleRental: {
                stations: []
            },
            zipcar: {
                locations: []
            }
        },
        ui: {
            mobileScreen: MobileScreens.WELCOME_SCREEN,
            printView: window.location.href.indexOf('/print/') !== -1,
            diagramLeg: null,
            isShowPriceScreen:false
        },
    }
}

function updateTilesURLConfigforLanguage(config,lang){
    let tilesURL = "https://api.mapbox.com/styles/v1/nadavmos/"
    if(lang === "ru"){
        tilesURL += "ckg929mkd73jk1as4dh2x1z06";
    }else if(lang === "ar"){
        tilesURL += "ckg951ded765i19qh5hk1w1yw"
    }else if(lang === "en"){
        tilesURL += "ckg965u3v0ct919s438trpxrj"
    }else{
        tilesURL += "ckg94xrm043cj19qu9wgfcnh7";
    }
    tilesURL += "/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoibmFkYXZtb3MiLCJhIjoiY2o4b3RtcDE0MDg4NTJxcWNhbWY5ZTMwMSJ9.6l2Nxahra0kwspnE6nPN-g";
    let baseLayers = [...config.map.baseLayers];
    baseLayers[0].url = tilesURL;
    config.map.baseLayers = baseLayers;
}

function createOtpReducer(config, initialQuery) {
    const initialState = getInitialState(config, initialQuery)

    // validate the inital state
    validateInitalState(initialState)

    return (state = initialState, action) => {
        const searchId = action.payload && action.payload.searchId
        switch (action.type) {
            case 'ROUTING_REQUEST':
                const {activeItinerary} = action.payload
                return update(state, {
                    searches: {
                        [searchId]: {
                            $set: {
                                activeItinerary,
                                activeLeg: null,
                                activeStep: null,
                                pending: true,
                                query: clone(state.currentQuery),
                                response: null
                            }
                        }
                    },
                    activeSearchId: {$set: searchId}
                })
            case 'ROUTING_ERROR':
                return update(state, {
                    searches: {
                        [searchId]: {
                            response: {
                                $set: {
                                    error: action.payload.error
                                }
                            },
                            pending: {$set: false}
                        }
                    }
                })
            case 'ROUTING_RESPONSE':
                const response = (state.currentQuery.routingType === 'PROFILE')
                    ? filterProfileOptions(action.payload.response)
                    : action.payload.response

                return update(state, {
                    searches: {
                        [searchId]: {
                            response: {$set: response},
                            pending: {$set: false}
                        }
                    },
                    ui: {
                        diagramLeg: {$set: false}
                    }
                })
            case 'NON_REALTIME_ROUTING_RESPONSE':
                return update(state, {
                    searches: {
                        [searchId]: {
                            nonRealtimeResponse: {$set: action.payload.response}
                        }
                    }
                })
            case 'PRICES_RESPONSE':
                    return update(state, {
                        activeItineraryPrices: {$set: action.payload.response}   
                        }
                    )
            case 'BIKE_RENTAL_REQUEST':
                return update(state, {
                    overlay: {
                        bikeRental: {
                            pending: {$set: true},
                            error: {$set: null}
                        }
                    }
                })
            case 'BIKE_RENTAL_ERROR':
                return update(state, {
                    overlay: {
                        bikeRental: {
                            pending: {$set: false},
                            error: {$set: action.payload}
                        }
                    }
                })
            case 'BIKE_RENTAL_RESPONSE':
                return update(state, {
                    overlay: {
                        bikeRental: {
                            stations: {$set: action.payload.stations},
                            pending: {$set: false}
                        }
                    }
                })
            case 'CAR_RENTAL_ERROR':
                return update(state, {
                    overlay: {
                        carRental: {
                            pending: {$set: false},
                            error: {$set: action.payload}
                        }
                    }
                })
            case 'CAR_RENTAL_RESPONSE':
                return update(state, {
                    overlay: {
                        carRental: {
                            stations: {$set: action.payload.stations},
                            pending: {$set: false}
                        }
                    }
                })
            case 'VEHICLE_RENTAL_ERROR':
                return update(state, {
                    overlay: {
                        vehicleRental: {
                            pending: {$set: false},
                            error: {$set: action.payload}
                        }
                    }
                })
            case 'VEHICLE_RENTAL_RESPONSE':
                return update(state, {
                    overlay: {
                        vehicleRental: {
                            stations: {$set: action.payload.stations},
                            pending: {$set: false}
                        }
                    }
                })
            case 'SET_USE_REALTIME_RESPONSE':
                return update(state, {
                    useRealtime: {$set: action.payload.useRealtime}
                })
            case 'SET_ACTIVE_ITINERARY':
                if (state.activeSearchId) {
                    return update(state, {
                        searches: {
                            [state.activeSearchId]: {
                                activeItinerary: {$set: action.payload ? action.payload.index : null},
                                activeLeg: {$set: null},
                                activeStep: {$set: null}
                            }
                        }
                    })
                }
                return state
            case 'SET_ACTIVE_LEG':
                if (state.activeSearchId !== null) {
                    return update(state, {
                        searches: {
                            [state.activeSearchId]: {
                                activeLeg: {$set: action.payload.index},
                                activeStep: {$set: null}
                            }
                        }
                    })
                }
                return state
            case 'SET_ACTIVE_STEP':
                if (state.activeSearchId !== null) {
                    return update(state, {
                        searches: {
                            [state.activeSearchId]: {
                                activeStep: {$set: action.payload.index}
                            }
                        }
                    })
                }
                return state
            case 'SET_LOCATION':
                return update(state, {
                    currentQuery: {
                        [action.payload.locationType]: {$set: action.payload.location}
                    }
                })
            case 'CLEAR_LOCATION':
                return update(state, {
                    currentQuery: {[action.payload.locationType]: {$set: null}}
                })
            case 'SET_QUERY_PARAM':
                return update(state, {currentQuery: {$merge: action.payload}})

            case 'CLEAR_ACTIVE_SEARCH':
                return update(state, {activeSearchId: {$set: null},activeItineraryPrices:{$set: null},searches:{$set: {}}})
            case 'SET_ACTIVE_SEARCH':
                return update(state, {activeSearchId: {$set: action.payload}})
            case 'CLEAR_DEFAULT_SETTINGS':
                removeItem('defaultQuery')
                return update(state, {user: {defaults: {$set: null}}})
            case 'STORE_DEFAULT_SETTINGS':
                storeItem('defaultQuery', action.payload)
                return update(state, {user: {defaults: {$set: action.payload}}})
            case 'FORGET_PLACE': {
                // Payload is the place ID.
                // Recent place IDs contain the string literal 'recent'.
                if (action.payload.indexOf('recent') !== -1) {
                    const recentPlaces = clone(state.user.recentPlaces)
                    // Remove recent from list of recent places
                    const removeIndex = recentPlaces.findIndex(l => l.id === action.payload)
                    recentPlaces.splice(removeIndex, 1)
                    storeItem('recent', recentPlaces)
                    return removeIndex !== -1
                        ? update(state, {user: {recentPlaces: {$splice: [[removeIndex, 1]]}}})
                        : state
                } else {
                    const locations = clone(state.user.locations)
                    const removeIndex = locations.findIndex(l => l.id === action.payload)
                    removeItem(action.payload)
                    return removeIndex !== -1
                        ? update(state, {user: {locations: {$splice: [[removeIndex, 1]]}}})
                        : state
                }
            }
            case 'REMEMBER_PLACE': {
                const {location, type} = action.payload
                switch (type) {
                    case 'recent': {
                        const recentPlaces = clone(state.user.recentPlaces)
                        const index = recentPlaces.findIndex(l => matchLatLon(l, location))
                        // Replace recent place if duplicate found or add to list.
                        if (index !== -1) recentPlaces.splice(index, 1, location)
                        else recentPlaces.push(location)
                        const sortedPlaces = recentPlaces.sort((a, b) => b.timestamp - a.timestamp)
                        // Only keep up to 5 recent locations
                        // FIXME: Check for duplicates
                        if (recentPlaces.length >= MAX_RECENT_STORAGE) {
                            sortedPlaces.splice(MAX_RECENT_STORAGE)
                        }
                        storeItem('recent', recentPlaces)
                        return update(state, {user: {recentPlaces: {$set: sortedPlaces}}})
                    }
                    default: {
                        const locations = clone(state.user.locations)
                        // Determine if location type (e.g., home or work) already exists in list
                        const index = locations.findIndex(l => l.type === type)
                        if (index !== -1) locations.splice(index, 1, location)
                        else locations.push(location)
                        storeItem(type, location)
                        return update(state, {user: {locations: {$set: locations}}})
                    }
                }
            }
            case 'FORGET_STOP': {
                // Payload is the stop ID.
                const favoriteStops = clone(state.user.favoriteStops)
                // Remove stop from favorites
                const removeIndex = favoriteStops.findIndex(l => l.id === action.payload)
                favoriteStops.splice(removeIndex, 1)
                storeItem('favoriteStops', favoriteStops)
                return removeIndex !== -1
                    ? update(state, {user: {favoriteStops: {$splice: [[removeIndex, 1]]}}})
                    : state
            }
            case 'REMEMBER_STOP': {
                // Payload is stop data. We want to avoid saving other attributes that
                // might be contained there (like lists of patterns).
                const {id, name, lat, lon} = action.payload
                const stop = {
                    type: 'stop',
                    icon: 'bus',
                    id,
                    name,
                    lat,
                    lon
                }
                const favoriteStops = clone(state.user.favoriteStops)
                if (favoriteStops.length >= MAX_RECENT_STORAGE) {
                    window.alert(`Cannot save more than ${MAX_RECENT_STORAGE} stops. Remove one before adding more.`)
                    return state
                }
                const index = favoriteStops.findIndex(s => s.id === stop.id)
                // Do nothing if duplicate stop found.
                if (index !== -1) {
                    console.warn(`Stop with id ${stop.id} already exists in favorites.`)
                    return state
                } else {
                    favoriteStops.unshift(stop)
                }
                storeItem('favoriteStops', favoriteStops)
                return update(state, {user: {favoriteStops: {$set: favoriteStops}}})
            }
            case 'TOGGLE_TRACKING': {
                storeItem('trackRecent', action.payload)
                let recentPlaces = clone(state.user.recentPlaces)
                let recentSearches = clone(state.user.recentSearches)
                if (!action.payload) {
                    // If user disables tracking, remove recent searches and locations.
                    recentPlaces = []
                    recentSearches = []
                    removeItem('recent')
                    removeItem('recentSearches')
                }
                return update(state, {
                    user: {
                        trackRecent: {$set: action.payload},
                        recentPlaces: {$set: recentPlaces},
                        recentSearches: {$set: recentSearches}
                    }
                })
            }
            case 'REMEMBER_SEARCH':
                const searches = clone(state.user.recentSearches)
                const duplicateIndex = searches.findIndex(s => isEqual(s.query, action.payload.query))
                // Overwrite duplicate search (so that new timestamp is stored).
                if (duplicateIndex !== -1) searches[duplicateIndex] = action.payload
                else searches.unshift(action.payload)
                const sortedSearches = searches.sort((a, b) => b.timestamp - a.timestamp)
                // Ensure recent searches do not extend beyong MAX_RECENT_STORAGE
                if (sortedSearches.length >= MAX_RECENT_STORAGE) {
                    sortedSearches.splice(MAX_RECENT_STORAGE)
                }
                storeItem('recentSearches', sortedSearches)
                return update(state, {user: {searches: {$set: sortedSearches}}})
            case 'FORGET_SEARCH': {
                const recentSearches = clone(state.user.recentSearches)
                const index = recentSearches.findIndex(l => l.id === action.payload)
                // Remove item from list of recent searches
                recentSearches.splice(index, 1)
                storeItem('recentSearches', recentSearches)
                return index !== -1
                    ? update(state, {user: {recentSearches: {$splice: [[index, 1]]}}})
                    : state
            }
            case 'SET_AUTOPLAN':
                return update(state, {
                    config: {autoPlan: {$set: action.payload.autoPlan}}
                })
            case 'SET_MAP_CENTER':
                return update(state, {
                    config: {
                        map: {
                            initLat: {$set: action.payload.lat},
                            initLon: {$set: action.payload.lon}
                        }
                    }
                })
            case 'SET_MAP_ZOOM':
                return update(state, {
                    config: {
                        map: {
                            initZoom: {$set: action.payload.zoom}
                        }
                    }
                })
            case 'SET_ROUTER_ID':
                const routerId = action.payload || 'default'
                return update(state, {
                    config: {
                        api: {
                            path: {$set: `/otp/routers/${routerId}`}
                        }
                    }
                })
            case 'SET_NEED_STOP_INFORMATION':
                const needInfo = action.payload;
                return update(state, {
                    config: {
                        needStopInformation:{$set:needInfo}
                        
                    }
                })
            case 'SHOW_LEG_DIAGRAM':
                return update(state, {
                    ui: {
                        diagramLeg: {$set: action.payload}
                    }
                })
            case 'SET_ELEVATION_POINT':
                return update(state, {
                    ui: {
                        elevationPoint: {$set: action.payload}
                    }
                })
            case 'SET_MAP_POPUP_LOCATION':
                return update(state, {
                    ui: {
                        mapPopupLocation: {$set: action.payload.location}
                    }
                })
            case 'POSITION_FETCHING':
                return update(state, {
                    location: {
                        currentPosition: {$merge: {fetching: true}}
                    }
                })
            case 'POSITION_ERROR':
                return update(state, {
                    location: {currentPosition: {$set: action.payload}}
                })
            case 'POSITION_RESPONSE':
                return update(state, {
                    location: {currentPosition: {$set: action.payload.position}}
                })
            case 'ADD_LOCATION_SEARCH':
                return update(state, {
                    location: {sessionSearches: {$unshift: [action.payload.location]}}
                })

            case 'NEARBY_STOPS_RESPONSE':
                const stopLookup = {}
                action.payload.stops.forEach(s => {
                    stopLookup[s.id] = s
                })
                return update(state, {
                    location: {
                        nearbyStops: {$set: action.payload.stops.map(s => s.id)}
                    },
                    transitIndex: {stops: {$merge: stopLookup}}
                })
            case 'STOPS_WITHIN_BBOX_RESPONSE':
                return update(state, {
                    overlay: {
                        transit: {
                            stops: {$set: action.payload.stops},
                            pending: {$set: false}
                        }
                    }
                })
            case 'CLEAR_STOPS_OVERLAY':
                return update(state, {
                    overlay: {
                        transit: {
                            stops: {$set: []},
                            pending: {$set: false}
                        }
                    }
                })
            case 'ROUTES_AT_STOP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        stops: {
                            [action.payload.stopId]: {
                                routes: {$set: action.payload.routes}
                            }
                        }
                    }
                })
            case 'SET_MOBILE_SCREEN':
                return update(state, {ui: {mobileScreen: {$set: action.payload}}})
            case 'SET_LANGUAGE':
               let  modes = {
                    transitModes: [
                        {
                            "mode": "BUS",
                            "label": i18n.t('bus')
                        },
                        {
                            "mode": "TRAM",
                            "label": i18n.t('maxStreetcar')
                        },
                        {
                            "mode": "RAIL",
                            "label": i18n.t('WES')
                        },
                        {
                            "mode": "GONDOLA",
                            "label": i18n.t('aerialTram')
                        }
                    ],
                        accessModes: [
                        {
                            "mode": "BICYCLE",
                            "label": i18n.t('transitAndBike')
                        }
                    ],
                        bicycleModes: [
                        {
                            "mode": "BICYCLE",
                            "label": "Own Bike",
                            "iconWidth": 18
                        }
                    ]
                }
                let dir = action.payload === "he" || action.payload === "ar" ? 'rtl' : 'ltr';
                let lang = action.payload;
                storeItem('userLanguage', lang);
                configuredDirectionByLanguage(lang);
                updateTilesURLConfigforLanguage(state.config, lang);
                i18n.changeLanguage(lang);
                let map = Object.assign({},state.config.map);
                return update(state, {config: {map:{$set:map}, userLanguage:{$set: lang}, dir:{$set: dir},modes :{$set: modes} }})
            case 'RESET_STATE':
                return initialState;
            case 'SET_MARKERS':
                 return update(state, {config: {isShowMarkers:{$set: action.payload}}});
            case 'SET_MAIN_PANEL_CONTENT':
                return update(state, {
                    ui: {
                        mainPanelContent: {$set: action.payload}
                    }
                })
            case 'SET_VIEWED_STOP':
                if (action.payload) {
                    // If setting to a stop (not null), also set main panel.
                    return update(state, {
                        ui: {
                            mainPanelContent: {$set: MainPanelContent.STOP_VIEWER},
                            viewedStop: {$set: action.payload}
                        }
                    })
                } else {
                    // Otherwise, just replace viewed stop with null
                    return update(state, {ui: {viewedStop: {$set: action.payload}}})
                }
            case 'CLEAR_VIEWED_STOP':
                return update(state, {ui: {viewedStop: {$set: null}}})

            case 'SET_VIEWED_TRIP':
                let updateObj = {ui: {viewedTrip: {$set: action.payload}}}
                if(null !== action.payload && null !== action.payload.tripId){
                    updateObj.transitIndex =  {trips: {[action.payload.tripId]: {$set: action.payload.leg}}}
                }
                return update(state, updateObj)
            case 'CLEAR_VIEWED_TRIP':
                return update(state, {ui: {viewedTrip: {$set: null}}})

            case 'SET_VIEWED_ROUTE':
                if (action.payload) {
                    // If setting to a route (not null), also set main panel.
                    return update(state, {
                        ui: {
                            mainPanelContent: {$set: MainPanelContent.ROUTE_VIEWER},
                            viewedRoute: {$set: action.payload}
                        }
                    })
                } else {
                    // Otherwise, just replace viewed route with null
                    return update(state, {ui: {viewedRoute: {$set: action.payload}}})
                }
                case 'SET_VIEWED_ROUTE_SELECTED_INDEX':
                        // Otherwise, just replace viewed route with null
                        return update(state, {ui: {viewedRoute:{selectedIndex: {$set: action.payload}}}})
                    
            case 'FIND_STOP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        stops: {[action.payload.id]: {$set: action.payload}}
                    }
                })
            case 'FIND_TRIP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        trips: {[action.payload.tripId]: {$set: action.payload}}
                    }
                })
            case 'FIND_STOPS_FOR_TRIP_RESPONSE':{
                let stops = {}
                action.payload.stops.forEach(stop => {
                    stops[stop.id] = {$set: stop}
                })

                return update(state, {
                    transitIndex: {
                        stops,
                        trips: {
                            [action.payload.tripId]: {stops: {$set: action.payload.stops}}
                        }
                    }
                })
            }
            case 'FIND_STOP_TIMES_FOR_TRIP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        trips: {
                            [action.payload.tripId]: {
                                stopTimes: {$set: action.payload.stopTimes}
                            }
                        }
                    }
                })
            case 'FIND_GEOMETRY_FOR_TRIP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        trips: {
                            [action.payload.tripId]: {
                                geometry: {$set: action.payload.geometry}
                            }
                        }
                    }
                })
            case 'FIND_STOP_TIMES_FOR_STOP_RESPONSE':
                return update(state, {
                    transitIndex: {
                        stops: {
                            [action.payload.stopId]: {
                                stopTimes: {$set: action.payload.stopTimes},
                                stopTimesLastUpdated: {$set: new Date().getTime()}
                            }
                        }
                    }
                })
            case 'TOGGLE_AUTO_REFRESH':
                storeItem('autoRefreshStopTimes', action.payload)
                return update(state, {user: {autoRefreshStopTimes: {$set: action.payload}}})
            case 'FIND_STATIONS_RESPONSE':{
                let stops = {}
                action.payload.forEach(stop => {
                    stops[stop.id] = {$set: stop}
                })
                return update(state, {
                    transitIndex: {stops:stops,stations: {$set: action.payload}}
                })
            }
            case 'FIND_ROUTES_RESPONSE':
                return update(state, {
                    transitIndex: {routesArray: {$set: action.payload}}
                })
            case 'SET_LINE_NUMBER':
                return update(state, {
                    transitIndex: {line: {$set: action.payload}}
                })
            case 'FIND_ROUTE_RESPONSE':
                // If routes is undefined, initialize it w/ this route only
                if (!state.transitIndex.routes) {
                    return update(state, {
                        transitIndex: {routes: {$set: {[action.payload.id]: action.payload}}}
                    })
                }
                // Otherwise, overwrite only this route
                return update(state, {
                    transitIndex: {
                        routes: {[action.payload.id]: {$set: action.payload}}
                    }
                })
                break;

            case 'FIND_ROUTE_ALERTS_RESPONSE':
                {
                const {alertPatches} = action.payload
                if (!state.transitIndex.routes || !alertPatches || !alertPatches.length) {
                    return state;
                }

                return update(state, {
                    transitIndex: {
                        routes: {[alertPatches[0].route]: {alerts: {$set: alertPatches}}}
                    }
                })

                }
                break;

            case 'FIND_STOP_ALERTS_RESPONSE':
                {
                const {alertPatches} = action.payload
                if (!alertPatches || !alertPatches.length) {
                    return state;
                }

            
                // Otherwise, overwrite only this route
                return update(state, {
                    transitIndex: {stopAlerts: {[alertPatches[0].stop]: {$set:alertPatches}}}
                })
                break;

               /* return update(state, {
                    transitIndex: {
                        stops: {[alertPatches[0].stop]: {alerts: {$set: alertPatches}}}
                    }
                })*/

                }
                break;



            case 'FIND_PATTERNS_FOR_ROUTE_RESPONSE':{
                const {patterns, routeId} = action.payload
                //insert all stops to transitIndex so no need to fetch them again later
                if(!patterns){
                    return state;
                }

                let stops = {}
                let route = null;
                Object.values(patterns).forEach(pattern => {
                    route = pattern.route;
                    if(pattern.stops){
                        pattern.stops.forEach(stop => {
                            stops[stop.id] = {$set: stop}
                        })  
                    }
                  })

                // If routes is undefined, initialize it w/ this route only
                if (!state.transitIndex.routes) {
                    route.patterns = patterns;
                    return update(state, {
                        transitIndex: {stops,routes: {$set: {[routeId]: route}}}
                    })
                }else if(!state.transitIndex.routes[routeId]){
                    route.patterns = patterns;
                    return update(state, {
                        transitIndex: {
                            stops,
                            routes: {[routeId]: {$set: route}}
                        }
                    })
                    

                }
                // Otherwise, overwrite only this route
                return update(state, {
                    transitIndex: {
                        stops,
                        routes: {[routeId]: {patterns: {$set: patterns}}}
                    }
                })
            }
            case 'FIND_GEOMETRY_FOR_PATTERN_RESPONSE':
                return update(state, {
                    transitIndex: {
                        routes: {
                            [action.payload.routeId]: {
                                patterns: {
                                    [action.payload.patternId]: {
                                        geometry: {$set: action.payload.geometry}
                                    }
                                }
                            }
                        }
                    }
                })

            case 'PARK_AND_RIDE_RESPONSE':
                return update(state, {
                    overlay: {
                        parkAndRide: {
                            locations: {$set: action.payload},
                            pending: {$set: false}
                        }
                    }
                })

            // TODO: can this be broken out into a separate module?
            case 'ZIPCAR_LOCATIONS_RESPONSE':
                return update(state, {
                    overlay: {
                        zipcar: {
                            locations: {$set: action.payload.locations},
                            pending: {$set: false}
                        }
                    }
                })
            case 'UPDATE_OVERLAY_VISIBILITY':
                const mapOverlays = clone(state.config.map.overlays)
                for (let key in action.payload) {
                    const overlay = mapOverlays.find(o => o.name === key)
                    overlay.visible = action.payload[key]
                }
                return update(state,
                    {config: {map: {overlays: {$set: mapOverlays}}}}
                )
            case 'FIND_SCHEDULES_ROUTE_FOR_STOPS_RESPONSE':
                return update(state, {
                    transitIndex: {schedules: {$set: action.payload}}
                });
            case 'FIND_ROUTE_DIRECTIONS_RESPONSE':
                return update(state, {
                    transitIndex: {routeDirections: {$set: action.payload}}
                });
            case 'RESET_ROUTE_DIRECTIONS':
                return update(state, {
                    transitIndex: {routeDirections: {$set: []}}
                });
            default:
                return state
            case 'SET_PRICE_SCREEN':
                return update(state,
                    {ui: {isShowPriceScreen: {$set: action.payload}}}
                )
        }
    }
}



export default createOtpReducer
